import { useCallback, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDebounce } from 'react-use';

import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';

import {
  CNAME_DEBOUNCE_IN_MS,
  COMPANY_NAME_DEBOUNCE_IN_MS,
} from '../OnboardingForm.constants';
import {
  getDomainSuffix,
  isValidCName,
  slugifyCompanyName,
} from '../OnboardingForm.helpers';

import messages from './messages';
import {
  ManualCheck,
  useCheckCNameAvailability,
} from './useCheckCNameAvailability';
import { ResolvedCName, useFindAvailableCName } from './useFindAvailableCName';
import { OnboardingFormFieldValues } from './useOnboardingFormContext';

interface PendingResolution {
  slug: string;
  promise: Promise<ResolvedCName | undefined>;
}

interface UseAutoCNameReturnValues {
  suggestedCName?: ResolvedCName;
  domainSuffix?: string;
  isResolvingCName: boolean;
  hasNoSuggestedCName: boolean;
  isCNameEdited: boolean;
  startEditingCName: () => void;
  restoreSuggestedCName: () => void;
  resolveCName: (companyName?: string) => Promise<ResolvedCName | undefined>;
}

export function useAutoCName({
  formContext,
}: {
  formContext: UseFormReturn<OnboardingFormFieldValues>;
}): UseAutoCNameReturnValues {
  const findAvailableCName = useFindAvailableCName();
  const checkCNameAvailability = useCheckCNameAvailability();
  const { watch, setValue, setError, clearErrors, getValues } = formContext;
  const [suggestedCName, setSuggestedCName] = useState<ResolvedCName>();
  const [manualCheck, setManualCheck] = useState<ManualCheck>();
  const [isCNameEdited, setIsCNameEdited] = useState(false);
  const [isResolvingCName, setIsResolvingCName] = useState(false);
  const [unresolvedCompanyName, setUnresolvedCompanyName] = useState<string>();
  const resolvedForSlugRef = useRef<string | undefined>(undefined);
  const pendingRef = useRef<PendingResolution | undefined>(undefined);

  const resolveSuggestedCName = useCallback(
    async (companyName?: string): Promise<ResolvedCName | undefined> => {
      const slug = slugifyCompanyName(companyName);

      if (!slug) {
        setUnresolvedCompanyName(companyName);

        return undefined;
      }

      if (resolvedForSlugRef.current === slug && suggestedCName) {
        return suggestedCName;
      }

      if (pendingRef.current?.slug === slug) {
        return pendingRef.current.promise;
      }

      setIsResolvingCName(true);

      const promise = findAvailableCName(slug).then((resolved) => {
        if (pendingRef.current?.slug !== slug) {
          return resolved;
        }

        pendingRef.current = undefined;
        resolvedForSlugRef.current = resolved ? slug : undefined;
        setSuggestedCName(resolved);
        setUnresolvedCompanyName(resolved ? undefined : companyName);
        setIsResolvingCName(false);

        return resolved;
      });

      pendingRef.current = { slug, promise };

      return promise;
    },
    [findAvailableCName, suggestedCName],
  );

  const applyManualCheck = useCallback(
    (check: ManualCheck | undefined) => {
      setManualCheck(check);

      if (check && !check.isAvailable) {
        setError('cName', {
          message: safeFormattedIntlString(messages.cNameAlreadyUsed),
        });

        return;
      }

      clearErrors('cName');
    },
    [clearErrors, setError],
  );

  const resolveManualCName = useCallback(async (): Promise<
    ResolvedCName | undefined
  > => {
    const cName = getValues('cName')?.trim() ?? '';

    if (!isValidCName(cName)) {
      setError('cName', {
        message: safeFormattedIntlString(messages.invalidCName),
      });

      return undefined;
    }

    const check =
      manualCheck?.cName === cName
        ? manualCheck
        : await checkCNameAvailability(cName);

    if (!check) {
      toast.error(messages.cNameAvailabilityError);

      return undefined;
    }

    applyManualCheck(check);

    return check.isAvailable
      ? { cName, domainName: check.domainName }
      : undefined;
  }, [
    applyManualCheck,
    checkCNameAvailability,
    getValues,
    manualCheck,
    setError,
  ]);

  const resolveCName = useCallback(
    (companyName?: string) =>
      isCNameEdited ? resolveManualCName() : resolveSuggestedCName(companyName),
    [isCNameEdited, resolveManualCName, resolveSuggestedCName],
  );

  const startEditingCName = useCallback(() => {
    setValue('cName', suggestedCName?.cName ?? '', { shouldValidate: false });
    setManualCheck(undefined);
    setIsCNameEdited(true);
  }, [setValue, suggestedCName]);

  const restoreSuggestedCName = useCallback(() => {
    setIsCNameEdited(false);
    setManualCheck(undefined);
    clearErrors('cName');
    setValue('cName', suggestedCName?.cName ?? '', { shouldValidate: false });
  }, [clearErrors, setValue, suggestedCName]);

  const watchedCompanyName = watch('companyName');
  const watchedCName = watch('cName');

  useDebounce(
    () => {
      if (watchedCompanyName && !isCNameEdited) {
        void resolveSuggestedCName(watchedCompanyName);
      }
    },
    COMPANY_NAME_DEBOUNCE_IN_MS,
    [watchedCompanyName, isCNameEdited],
  );

  useDebounce(
    () => {
      const cName = watchedCName?.trim() ?? '';

      if (
        !isCNameEdited ||
        !isValidCName(cName) ||
        manualCheck?.cName === cName
      ) {
        return;
      }

      void checkCNameAvailability(cName).then((check) => {
        if (getValues('cName')?.trim() !== cName) {
          return;
        }

        applyManualCheck(check);
      });
    },
    CNAME_DEBOUNCE_IN_MS,
    [watchedCName, isCNameEdited, checkCNameAvailability],
  );

  const domainSuffix = suggestedCName
    ? getDomainSuffix(suggestedCName)
    : undefined;

  return {
    suggestedCName,
    domainSuffix,
    isResolvingCName,
    hasNoSuggestedCName:
      !!watchedCompanyName && unresolvedCompanyName === watchedCompanyName,
    isCNameEdited,
    startEditingCName,
    restoreSuggestedCName,
    resolveCName,
  };
}
